import { WorkflowPayload, DynamicActivityDefinition, Variable, Output, Input } from "./types"

// UI Activity Types that directly map to backend types - Needed
export const ACTIVITY_TYPES = {
  WRITE_LINE: 'WriteLine',
  DELAY: 'Delay',
  TIMER: 'Timer',
  SEND_EMAIL_MARKETING: 'Alle.Platform.Workflows.Activities.SendEmailMarketing',
  SEND_MESSAGE_REMINDER: 'Alle.Platform.Workflows.Activities.SendMessageReminder',
  READ_LINE: 'ReadLine',
  CRON: 'Cron',
  START_AT: 'StartAt',
  IF: 'If',
  WHILE: 'While',
  SWITCH: 'Switch',
  FLOW_SEND_HTTP_REQUEST: 'FlowSendHttpRequest',
  WRITE_HTTP_RESPONSE: 'WriteHttpResponse',
  HTTP_ENDPOINT: 'HttpEndpoint',
  DISPATCH_WORKFLOW: 'DispatchWorkflow',
  SET_OUTPUT: 'SetOutput',
  SET_VARIABLE: 'SetVariable',
  BREAK: 'Break',
  EVENT: 'Event',
} as const

// Helper functions for parameter formatting
export const formatTimeSpan = (duration: number, unit: string): string => {
  switch (unit) {
    case 'seconds':
      return `00:00:${duration.toString().padStart(2, '0')}`
    case 'minutes':
      return `00:${duration.toString().padStart(2, '0')}:00`
    case 'hours':
      return `${duration.toString().padStart(2, '0')}:00:00`
    case 'days':
      const totalHours = duration * 24
      return `${totalHours.toString().padStart(2, '0')}:00:00`
    default:
      return '00:00:00'
  }
}

// No conversion needed - UI directly creates backend-compatible format
export const CreateWorkflowPayload = (
  name: string,
  description: string,
  activities: DynamicActivityDefinition[],
  variables?: Variable[],
  outputs?: Output[],
  inputs?: Input[],
  links?: { from: string; to: string }[]
): WorkflowPayload => {
  return {
    name,
    description,
    activities,
    variables: variables || [],
    outputs: outputs || [],
    inputs: inputs || [],
    links: links || []
  }
}

export const getOrderedActivitiesFromLinks = (
  nodes: { id: string; activity: DynamicActivityDefinition }[],
  links: { from: string; to: string }[]
): DynamicActivityDefinition[] => {
  const adj: Record<string, string[]> = {};
  const inDegree: Record<string, number> = {};

  // Create a set of linked node IDs to filter nodes  
  const linkedNodeIds = new Set<string>();
  links.forEach(link => {
    linkedNodeIds.add(link.from);
    linkedNodeIds.add(link.to);
  });

  // Filter nodes to only include those that are linked
  const filteredNodes = nodes.filter(n => linkedNodeIds.has(n.id));

  // Initialize adjacency list and in-degree count
  filteredNodes.forEach(n => {
    adj[n.id] = [];
    inDegree[n.id] = 0;
  });

  // Build the graph from the links
  links.forEach(link => {
    if (adj[link.from]) {
      adj[link.from].push(link.to);
      inDegree[link.to]++;
    }
  });


  const queue: string[] = Object.keys(inDegree).filter(id => inDegree[id] === 0);
  const ordered: string[] = [];

  // Kahn's algorithm for topological sorting
  while (queue.length) {
    const id = queue.shift()!;
    ordered.push(id);
    adj[id].forEach(nei => {
      inDegree[nei]--;
      if (inDegree[nei] === 0) queue.push(nei);
    });
  }

  return ordered.map(id => filteredNodes.find(n => n.id === id)!.activity);
};
